/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1904279553 — Відновлення сесії після перезавантаження */

import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { useAppStore } from '../../../shared/hooks/useAppStore';
import { debug } from '../../../shared/lib/general';
import { checkQrData, getOrderInfo } from '../api/order.api';
import { ORDER_ROUTES } from '../order.constants';

/**
 * Rehydrate useAppStore from the URL `:id` order-id path param on mount.
 * Used by post-Connecting screens (SelectProduct, Payment*, Wait, Pulse-family,
 * CarWash) so that a browser reload doesn't leave them with DEFAULT_ORDER /
 * DEFAULT_VM. Mirrors the ?oid= recovery added to Connecting, but for routes
 * where the order id is the path segment.
 *
 * Returns hydrating=true while fetching; callers should short-circuit render
 * until false. On failure (order not found / vm not found / network error),
 * navigates to /:id/error so the user lands on a recognised terminal screen.
 */
export const useHydrateOrderFromUrl = () => {
    const { id } = useParams();
    const order = useAppStore((s) => s.order);
    const setOrder = useAppStore((s) => s.setOrder);
    const setVm = useAppStore((s) => s.setVm);
    const setQrCode = useAppStore((s) => s.setQrCode);
    const navigate = useNavigate();

    const needsHydration = !order.id && Boolean(id);
    const [hydrating, setHydrating] = useState<boolean>(needsHydration);

    useEffect(() => {
        if (!needsHydration) return;
        let cancelled = false;

        const goToError = () =>
            navigate(generatePath(ORDER_ROUTES.error, { id: 'order' }), {
                replace: true,
            });

        (async () => {
            try {
                debug(`Hydrate from URL :id=${id}`, 'info');
                const orderInfo = await getOrderInfo(String(id));
                if (cancelled) return;
                if (!orderInfo?.id || !orderInfo.qr) {
                    debug('Hydrate: invalid order info', 'warn', orderInfo);
                    goToError();
                    return;
                }

                const vmData = await checkQrData(orderInfo.qr);
                if (cancelled) return;
                if (!vmData?.qr) {
                    debug('Hydrate: invalid vm data', 'warn', vmData);
                    goToError();
                    return;
                }

                setOrder(orderInfo);
                setQrCode(orderInfo.qr);
                setVm(vmData);
                setHydrating(false);
            } catch (e: unknown) {
                if (cancelled) return;
                debug('Hydrate failed', 'error', e);
                goToError();
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return { hydrating };
};
